import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { FleetTile, DOT, WORD } from './FleetTile.jsx';

// ─── Operations → Pis ──────────────────────────────────────────────────────
// Every store Raspberry Pi (menus and KDS) from its last heartbeat: up or not,
// power, temperature, network, printers. CJ 2026-10-01, empire-kds-label D296:
// owners and managers only (server: view_fleet); adding a Pi is owners only
// (manage_fleet). Spec: empire-kds-label docs/SPEC-pi-dashboard.md.
//
// CJ 2026-10-06: the page is a quick-look grid of tiles (FleetTile.jsx). The
// full DeviceCard below is the detail for whichever tile is picked, and the
// add-a-Pi form sits behind a button rather than always open.

const AGENT_URL = 'https://monarch.empireteaandcoffee.com/api/fleet/heartbeat';

function ago(iso) {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - new Date(iso)) / 1000);
  if (s < 90) return `${Math.round(s)} s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 172800) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
}

function uptime(s) {
  if (s == null) return '—';
  const d = Math.floor(s / 86400); const h = Math.floor((s % 86400) / 3600);
  return d ? `${d}d ${h}h` : `${h}h ${Math.floor((s % 3600) / 60)}m`;
}

function Fact({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="text-xs text-gray-500">{label}</div>
      <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</div>
    </div>
  );
}

function DeviceCard({ d, canManage, onRevoke, onSaved, onClose }) {
  const [editing, setEditing] = useState(false);
  const hb = d.last_heartbeat || {};
  const sys = hb.system || {};
  const net = hb.network || {};
  const kds = hb.kds;
  const level = d.status.level;
  return (
    <div className="card" style={{ borderLeft: `5px solid ${DOT[level]}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            <span style={{ color: DOT[level] }}>●</span> {d.name}
            <span className="text-xs text-gray-500" style={{ fontWeight: 400 }}>
              {' '}· {d.location_name || 'No store'} · {(d.roles || []).map((r) => r.toUpperCase()).join(' + ')}
            </span>
          </div>
          <div className="text-xs text-gray-500">{WORD[level]} · heard from {ago(d.last_seen_at)}</div>
        </div>
        {!editing && (
          <div style={{ display: 'flex', gap: 6 }}>
            {canManage && <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setEditing(true)}>Edit</button>}
            {canManage && <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => onRevoke(d)}>Remove</button>}
            <button className="btn-secondary" style={{ fontSize: 12 }} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
      {editing && <EditPi d={d} onDone={(saved) => { setEditing(false); if (saved) onSaved(); }} />}
      {d.status.problems.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
          {d.status.problems.map((p, i) => (
            <li key={i} style={{ color: p.level === 'down' ? '#c62828' : '#8d6e00' }}>{p.text}</li>
          ))}
        </ul>
      )}
      {d.last_seen_at && (
        <div style={{ display: 'grid', gap: 8, marginTop: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
          <Fact label="Temperature">{sys.temp_c != null ? `${sys.temp_c}°C` : '—'}</Fact>
          <Fact label="Power">{sys.power?.undervoltage_now ? 'Under-voltage!' : sys.power?.undervoltage_since_boot ? 'Dipped since boot' : 'Good'}</Fact>
          <Fact label="Up for">{uptime(sys.uptime_s)}</Fact>
          <Fact label="Disk free">{sys.disk_gb ? `${sys.disk_gb.free} of ${sys.disk_gb.total} GB` : '—'}</Fact>
          <Fact label="Network">{net.kind === 'wifi' ? `WiFi ${net.ssid || ''}` : net.kind === 'ethernet' ? 'Ethernet' : '—'}</Fact>
          <Fact label="Address">{net.lan_ip || '—'}{net.tailscale_ip ? ` · TS ${net.tailscale_ip}` : ''}</Fact>
          <Fact label="USB">{(hb.usb || []).filter((u) => u.kind !== 'other').map((u) => u.kind).join(', ') || 'none'}</Fact>
          {kds && kds.reachable && (
            <>
              <Fact label="KDS">{kds.station?.name} · {String(kds.station?.mode || '').toUpperCase()}</Fact>
              <Fact label="Square">{kds.square?.state || '—'}</Fact>
              <Fact label="Printers">
                {(kds.printers || []).map((p) => `${p.name} ${p.state === 'ready' ? '✓' : p.state}`).join(' · ') || 'none'}
              </Fact>
              <Fact label="Labels today">
                {kds.print_jobs_today?.printed ?? 0} printed{kds.print_jobs_today?.failed ? `, ${kds.print_jobs_today.failed} failed` : ''}
              </Fact>
              <Fact label="Last order">{ago(kds.last_order_paid_at)}</Fact>
              <Fact label="Version">{kds.build || kds.version || '—'}</Fact>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const LABEL = { fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 };
const FIELD = { border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13, background: '#fff',
                width: '100%', boxSizing: 'border-box' };

const ROLE_TEXT = [['kds', 'KDS + printers'], ['menu', 'Menu screens']];

function RolePicker({ roles, setRoles }) {
  const toggle = (r) => setRoles((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {ROLE_TEXT.map(([r, text]) => (
        <button key={r} type="button" onClick={() => toggle(r)}
          className={roles.includes(r) ? 'btn-primary' : 'btn-secondary'}
          style={{ fontSize: 13, flex: 1 }} aria-pressed={roles.includes(r)}>
          {roles.includes(r) ? '✓ ' : ''}{text}
        </button>
      ))}
    </div>
  );
}

// CJ 2026-10-04: a Bristol menu Pi was added as KDS + MENU and showed a KDS error;
// roles were fixed at creation. Change them (and the name) in place, same token.
function EditPi({ d, onDone }) {
  const [roles, setRoles] = useState(d.roles || []);
  const [name, setName] = useState(d.name);
  const [error, setError] = useState('');
  const missing = roles.length === 0 ? 'Pick what it runs.' : !name.trim() ? 'Give it a name.' : '';
  async function save() {
    setError('');
    try {
      await api('PATCH', `/api/fleet/devices/${d.id}`, { name: name.trim(), roles });
      onDone(true);
    } catch (e) { setError(e.message || 'Could not save'); }
  }
  return (
    <div style={{ display: 'grid', gap: 10, marginTop: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
      <div style={LABEL}>
        What it runs
        <RolePicker roles={roles} setRoles={setRoles} />
      </div>
      <label style={LABEL}>
        Name
        <input value={name} maxLength={60} style={FIELD} onChange={(e) => setName(e.target.value)} />
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, gridColumn: '1 / -1' }}>
        <button className="btn-primary" disabled={Boolean(missing)} onClick={save}>Save</button>
        <button className="btn-secondary" onClick={() => onDone(false)}>Cancel</button>
        {missing && <span className="text-xs text-gray-500">{missing}</span>}
        {error && <span style={{ color: '#b00020', fontSize: 12 }}>{error}</span>}
      </div>
    </div>
  );
}

// CJ 2026-10-01: the first version's name box had no label or border and read as
// blank space, so "Add" sat greyed out with no clue why. Labeled fields in the
// house style, the name filled in from the store, and a line saying what's missing.
function AddPi({ locations, onAdded }) {
  const [loc, setLoc] = useState('');
  const [roles, setRoles] = useState(['kds']);
  const [name, setName] = useState('');
  const [named, setNamed] = useState(false); // the person typed their own name
  const [made, setMade] = useState(null);
  const [error, setError] = useState('');
  const store = (locations || []).find((l) => String(l.id) === loc);
  const suggested = store ? `${store.name} ${roles.map((r) => r.toUpperCase()).join(' + ')}`.trim() : '';
  const finalName = named ? name : suggested;
  const missing = !loc ? 'Pick the store.' : roles.length === 0 ? 'Pick what it runs.'
    : !finalName.trim() ? 'Give it a name.' : '';
  async function add() {
    setError('');
    try {
      const r = await api('POST', '/api/fleet/devices', {
        name: finalName.trim(), location_id: Number(loc), roles,
      });
      setMade(r); setLoc(''); setName(''); setNamed(false); onAdded();
    } catch (e) { setError(e.message || 'Could not add'); }
  }
  if (made) {
    return (
      <div className="card">
        <div className="text-base font-semibold">“{made.name}” added</div>
        <p className="text-sm">On that Pi, run this, then paste the token when it asks. <b>The token is shown only once.</b></p>
        <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 8 }}>
          {`sudo ~/empire-kds-label/deploy/agent/install-agent.sh --roles ${made.roles.join(',')} --url ${AGENT_URL}`}
        </pre>
        <div className="text-xs text-gray-500">Token</div>
        <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 8, userSelect: 'all' }}>{made.token}</pre>
        <button className="btn-secondary" onClick={() => setMade(null)}>Done</button>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="text-base font-semibold" style={{ marginBottom: 8 }}>Add a Pi</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <label style={LABEL}>
          Store
          <select value={loc} onChange={(e) => setLoc(e.target.value)} style={FIELD}>
            <option value="">Pick a store…</option>
            {(locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        <div style={LABEL}>
          What it runs
          <RolePicker roles={roles} setRoles={setRoles} />
        </div>
        <label style={LABEL}>
          Name
          <input value={finalName} maxLength={60} placeholder="e.g. JT Connell KDS" style={FIELD}
            onChange={(e) => { setName(e.target.value); setNamed(true); }} />
        </label>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button className="btn-primary" disabled={Boolean(missing)} onClick={add}>Add Pi</button>
        {missing && <span className="text-xs text-gray-500">{missing}</span>}
        {error && <span style={{ color: '#b00020', fontSize: 12 }}>{error}</span>}
      </div>
    </div>
  );
}

export function FleetView({ locations, isOwner }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [adding, setAdding] = useState(false);

  async function load() {
    try { setData(await api('GET', '/api/fleet/devices')); setError(''); }
    catch (e) { setError(e.message || 'Failed to load'); }
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  async function revoke(d) {
    if (!window.confirm(`Remove “${d.name}”? Its heartbeats stop being accepted.`)) return;
    try { await api('POST', `/api/fleet/devices/${d.id}/revoke`); load(); }
    catch (e) { setError(e.message || 'Could not remove'); }
  }

  const devices = data?.devices || [];
  const selected = devices.find((d) => d.id === selectedId);
  const counts = devices.reduce((c, d) => ({ ...c, [d.status.level]: (c[d.status.level] || 0) + 1 }), {});
  return (
    <div className="space-y-4">
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="text-base font-semibold">Pis</div>
          <div className="text-xs text-gray-500">
            Every store Pi from its last heartbeat (once a minute). Refreshes every 30 seconds.
            {devices.length > 0 && ` ${counts.ok || 0} OK · ${counts.warn || 0} to check · ${counts.down || 0} with a problem.`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {isOwner && (
            <button onClick={() => setAdding((a) => !a)} className="btn-secondary" style={{ fontSize: 12 }}>
              {adding ? 'Close add' : 'Add a Pi'}
            </button>
          )}
          <button onClick={load} className="btn-secondary" style={{ fontSize: 12 }}>Refresh now</button>
        </div>
      </div>
      {error && <div className="card" style={{ color: '#b00020' }}>Error: {error}</div>}
      {isOwner && adding && <AddPi locations={locations} onAdded={load} />}
      {data && devices.length === 0 && <div className="card">No Pis yet.{isOwner ? ' Use “Add a Pi” above.' : ''}</div>}
      {devices.length > 0 && (
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {devices.map((d) => (
            <FleetTile key={d.id} d={d} selected={d.id === selectedId}
              onSelect={() => setSelectedId(d.id === selectedId ? null : d.id)} />
          ))}
        </div>
      )}
      {selected
        ? <DeviceCard key={selected.id} d={selected} canManage={isOwner} onRevoke={revoke} onSaved={load} onClose={() => setSelectedId(null)} />
        : devices.length > 0 && <div className="text-xs text-gray-500">Pick a Pi to see its details.</div>}
    </div>
  );
}
